import { Symbol3DMetadata } from "../../../../types/floorplannerTypes";
import { FloorPlan3DMaterialSlot } from "../../products/product3DTypes";
import {
  DINING_ARMCHAIR_SIZE_M,
  DINING_CHAIR_DEPTH_M,
  DINING_CHAIR_WIDTH_M,
} from "../dining/diningLayoutConstants";
import { DINING_SYMBOL_LIBRARY_DIMENSIONS } from "../dining/diningSymbolSvgGenerator";
import {
  SMART_RECT_TABLE_LIBRARY_KEY,
  SMART_RECT_TABLE_MODEL_KEY,
  SMART_ROUND_TABLE_LIBRARY_KEY,
  SMART_ROUND_TABLE_MODEL_KEY,
} from "../dining/smartTableConfig";
import {
  BOOK_SHELF_DEFAULT_DEPTH_M,
  BOOK_SHELF_DEFAULT_WIDTH_M,
  BOOK_SHELF_LIBRARY_KEY,
  BOOK_SHELF_MODEL_KEY,
  createDefaultBookShelfConfig,
} from "../livingRoom/bookShelfConfig";
import {
  BONNIE_DEPTH_M,
  BONNIE_HEIGHT_M,
  BONNIE_WIDTH_M,
} from "../primitives/livingRoom/bonnieConstants";

export type PrimitiveSymbol3DModelKey =
  | "living:sofa-2-seat"
  | "living:sofa-3-seat"
  | "living:sofa-modular"
  | "living:sofa-chaise"
  | "living:lounge-chair-1"
  | "living:lounge-chair-2"
  | "living:armchair"
  | "living:coffee-table-octagonal"
  | "living:coffee-table-pentagonal"
  | "living:coffee-table-hexagonal"
  | "living:coffee-table-organic"
  | "living:coffee-table-oval"
  | "living:screen"
  | "living:screen-wide"
  | "botanical:bush-round"
  | "botanical:bush-hedge"
  | "botanical:bush-organic"
  | "botanical:plant"
  | "botanical:tree-canopy"
  | "botanical:tree-branch"
  | "bedroom:bed-king"
  | "bedroom:bed-single"
  | "office:screen"
  | "office:screen-wide"
  | "bathroom:basin"
  | "bathroom:bathtub"
  | "bathroom:shower"
  | "bathroom:toilet"
  | "kitchen:cabinet"
  | "kitchen:fridge-k"
  | "kitchen:fridge-f"
  | "kitchen:sink-double"
  | "kitchen:sink-drainboard"
  | "kitchen:sink-single"
  | "kitchen:stove"
  | "kitchen:stove-compact"
  | "kitchen:stove-placeholder-angled"
  | "kitchen:stove-placeholder-simple"
  | "dining:table-plain"
  | "dining:table-2-chair"
  | "dining:table-4-chair"
  | "dining:table-6-chair"
  | "dining:armchair"
  | "dining:chair"
  | "dining:round-table-plain"
  | "dining:round-table"
  | "dining:smart-table-rectangle"
  | "dining:smart-table-round"
  | "living:bookshelf"
  | "symbols:curtain"
  | "symbols:bonnie"
  | "symbols:hangers-1"
  | "symbols:hangers-2"
  | "symbols:hangers-3"
  | "symbols:hangers-4"
  | "symbols:lighting"
  | "symbols:spotlight"
  | "symbols:lighting-fixture"
  | "symbols:ungrounded-power-outlet";

export type PrimitiveSymbol3DDefaults = Required<
  Pick<Symbol3DMetadata, "height" | "baseElevation" | "collision">
> & {
  dimensions: NonNullable<Symbol3DMetadata["dimensions"]>;
  modelKey: PrimitiveSymbol3DModelKey;
};

export type PrimitiveSymbol3DReferenceHeight = {
  measurement: "seatTop" | "mattressTop" | "rimTop" | "basinTop" | "tabletop";
  height: number;
};

export const PRIMITIVE_SYMBOL_3D_DEFAULTS_VERSION = 8;

export const PRIMITIVE_SYMBOL_LIBRARY_MODEL_KEYS: Record<
  string,
  PrimitiveSymbol3DModelKey
> = {
  "MANUAL/Lounge-2-seat-sofa": "living:sofa-2-seat",
  "MANUAL/Lounge-3-seat-sofa": "living:sofa-3-seat",
  "MANUAL/Modular-sofa": "living:sofa-modular",
  "MANUAL/Chaise-sofa": "living:sofa-chaise",
  "MANUAL/Lounge-chair-1": "living:lounge-chair-1",
  "MANUAL/Lounge-chair-2": "living:lounge-chair-2",
  "MANUAL/Armchair": "living:armchair",
  "MANUAL/Octagonal-coffee-table": "living:coffee-table-octagonal",
  "MANUAL/Pentagonal-coffee-table": "living:coffee-table-pentagonal",
  "MANUAL/Hexagonal-coffee-table": "living:coffee-table-hexagonal",
  "MANUAL/Oval-coffee-table": "living:coffee-table-organic",
  "MANUAL/Oval-coffee-table-2": "living:coffee-table-oval",
  "FLOOR_PLAN/LIVING ROOM/Screen": "living:screen",
  "FLOOR_PLAN/LIVING ROOM/Screen2": "living:screen-wide",
  "FLOOR_PLAN/BOTANICALS/Bush7": "botanical:bush-round",
  "FLOOR_PLAN/BOTANICALS/Bush8": "botanical:bush-hedge",
  "FLOOR_PLAN/BOTANICALS/Bush9": "botanical:bush-organic",
  "MANUAL/plant": "botanical:plant",
  "FLOOR_PLAN/BOTANICALS/Tree10": "botanical:tree-canopy",
  "FLOOR_PLAN/BOTANICALS/Tree11": "botanical:tree-branch",
  "MANUAL/king-bed": "bedroom:bed-king",
  "MANUAL/single-bed": "bedroom:bed-single",
  "FLOOR_PLAN/OFFICE/Screen3": "office:screen",
  "FLOOR_PLAN/OFFICE/Screen4": "office:screen-wide",
  "MANUAL/basin": "bathroom:basin",
  "MANUAL/bathtub": "bathroom:bathtub",
  "MANUAL/shower": "bathroom:shower",
  "MANUAL/toilet": "bathroom:toilet",
  "FLOOR_PLAN/KITCHEN/Cabinet5": "kitchen:cabinet",
  "FLOOR_PLAN/KITCHEN/Fridge4": "kitchen:fridge-k",
  "FLOOR_PLAN/KITCHEN/Fridge5": "kitchen:fridge-f",
  "FLOOR_PLAN/KITCHEN/Sink": "kitchen:sink-double",
  "FLOOR_PLAN/KITCHEN/Sink4": "kitchen:sink-drainboard",
  "FLOOR_PLAN/KITCHEN/Sink5": "kitchen:sink-single",
  "FLOOR_PLAN/KITCHEN/Stove": "kitchen:stove",
  "FLOOR_PLAN/KITCHEN/Stove3": "kitchen:stove-compact",
  "FLOOR_PLAN/KITCHEN/Stove4": "kitchen:stove-placeholder-angled",
  "FLOOR_PLAN/KITCHEN/Stove5": "kitchen:stove-placeholder-simple",
  "MANUAL/plain-table": "dining:table-plain",
  "MANUAL/2-chair-table": "dining:table-2-chair",
  "MANUAL/4-chair-table": "dining:table-4-chair",
  "MANUAL/6-chair-table": "dining:table-6-chair",
  "MANUAL/armchair-dining": "dining:armchair",
  "MANUAL/chair": "dining:chair",
  "MANUAL/plain-round-table": "dining:round-table-plain",
  "MANUAL/round-table": "dining:round-table",
  [SMART_RECT_TABLE_LIBRARY_KEY]: SMART_RECT_TABLE_MODEL_KEY,
  [SMART_ROUND_TABLE_LIBRARY_KEY]: SMART_ROUND_TABLE_MODEL_KEY,
  [BOOK_SHELF_LIBRARY_KEY]: BOOK_SHELF_MODEL_KEY,
  "FLOOR_PLAN/SYMBOLS & SIGNS/Curtain": "symbols:curtain",
  "FLOOR_PLAN/SYMBOLS & SIGNS/Bonnie": "symbols:bonnie",
  "FLOOR_PLAN/SYMBOLS & SIGNS/Lighting": "symbols:lighting",
  "FLOOR_PLAN/SYMBOLS & SIGNS/Ungrounded power outlet":
    "symbols:ungrounded-power-outlet",
  "MANUAL/spotlight": "symbols:spotlight",
  "MANUAL/lighting-fixture": "symbols:lighting-fixture",
  "MANUAL/hangers-1": "symbols:hangers-1",
  "MANUAL/hangers-2": "symbols:hangers-2",
  "MANUAL/hangers-3": "symbols:hangers-3",
  "MANUAL/hangers-4": "symbols:hangers-4",
};

/**
 * Yaw corrections (radians) for symbols whose 3D asset is authored facing a
 * different way than the 2D plan symbol. The king bed's asset is modelled with
 * the headboard toward +Y while the 2D symbol draws it at -X, so it needs a
 * quarter turn. Any admin `modelYawOffsetDeg` default overrides these.
 */
export const SYMBOL_3D_MODEL_YAW_OFFSETS: Partial<
  Record<PrimitiveSymbol3DModelKey, number>
> = {
  "bedroom:bed-king": Math.PI / 2,
};

export const PRIMITIVE_SYMBOL_MODEL_KEYS = new Set<PrimitiveSymbol3DModelKey>(
  Object.values(PRIMITIVE_SYMBOL_LIBRARY_MODEL_KEYS),
);

const PRIMITIVE_TABLE_3D_MODEL_KEYS = new Set<PrimitiveSymbol3DModelKey>([
  "living:coffee-table-octagonal",
  "living:coffee-table-pentagonal",
  "living:coffee-table-hexagonal",
  "living:coffee-table-organic",
  "living:coffee-table-oval",
  "dining:table-plain",
  "dining:table-2-chair",
  "dining:table-4-chair",
  "dining:table-6-chair",
  "dining:round-table-plain",
  "dining:round-table",
  "dining:smart-table-rectangle",
  "dining:smart-table-round",
]);

type PrimitiveSymbolLike = {
  libraryKey?: string;
  name?: string;
  svgPath?: string;
  svgLength?: number;
  svgHeight?: number;
  polygonWidth?: number;
  polygonHeight?: number;
  threeD?: Symbol3DMetadata;
};

type LegacyPrimitiveSymbolAlias = {
  libraryKey: keyof typeof PRIMITIVE_SYMBOL_LIBRARY_MODEL_KEYS;
  names: string[];
  pathFragments: string[];
};

const LEGACY_PRIMITIVE_SYMBOL_ALIASES: LegacyPrimitiveSymbolAlias[] = [
  {
    libraryKey: "MANUAL/Lounge-2-seat-sofa",
    names: ["2 seat sofa"],
    pathFragments: ["2-sist soffa-01", "2 seat sofa"],
  },
  {
    libraryKey: "MANUAL/Lounge-3-seat-sofa",
    names: ["3 seat sofa"],
    pathFragments: ["3 sists soffa-01", "3 seat sofa"],
  },
  {
    libraryKey: "MANUAL/Modular-sofa",
    names: ["modular sofa"],
    pathFragments: ["modulsoffa-01", "modular sofa"],
  },
  {
    libraryKey: "MANUAL/Chaise-sofa",
    names: ["chaise sofa"],
    pathFragments: ["schaslong-01", "chaise sofa"],
  },
  {
    libraryKey: "MANUAL/Lounge-chair-1",
    names: ["lounge chair"],
    pathFragments: ["fatolj 3-01", "lounge chair 1"],
  },
  {
    libraryKey: "MANUAL/Lounge-chair-2",
    names: ["lounge chair"],
    pathFragments: ["fatolj 2-01", "lounge chair 2"],
  },
  {
    libraryKey: "MANUAL/Armchair",
    names: ["armchair"],
    pathFragments: ["fatolj standard-01", "armchair 1"],
  },
  {
    libraryKey: "MANUAL/Octagonal-coffee-table",
    names: ["octagonal coffee table"],
    pathFragments: ["poly 1-01", "octagon"],
  },
  {
    libraryKey: "MANUAL/Pentagonal-coffee-table",
    names: ["pentagonal coffee table"],
    pathFragments: ["poly 2-01", "pentagon"],
  },
  {
    libraryKey: "MANUAL/Hexagonal-coffee-table",
    names: ["hexagonal coffee table"],
    pathFragments: ["poly 3-01", "hexagon"],
  },
  {
    libraryKey: "MANUAL/Oval-coffee-table",
    names: ["coffee table 1"],
    pathFragments: ["soffbord 2-01", "coffee table 1"],
  },
  {
    libraryKey: "MANUAL/Oval-coffee-table-2",
    names: ["coffee table 2"],
    pathFragments: ["matbord-01", "oval coffee table2", "coffee table 2"],
  },
  {
    libraryKey: "FLOOR_PLAN/LIVING ROOM/Screen",
    names: ["screen"],
    pathFragments: ["living room/screen", "screen.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/LIVING ROOM/Screen2",
    names: ["screen2", "screen 2"],
    pathFragments: ["living room/screen2", "screen2.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/BOTANICALS/Bush7",
    names: ["bush7", "bush 7"],
    pathFragments: ["botanicals/bush7", "bush7.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/BOTANICALS/Bush8",
    names: ["bush8", "bush 8"],
    pathFragments: ["botanicals/bush8", "bush8.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/BOTANICALS/Bush9",
    names: ["bush9", "bush 9"],
    pathFragments: ["botanicals/bush9", "bush9.svg"],
  },
  {
    libraryKey: "MANUAL/plant",
    names: ["plant"],
    pathFragments: ["vaxt", "plant 2"],
  },
  {
    libraryKey: "FLOOR_PLAN/BOTANICALS/Tree10",
    names: ["tree10", "tree 10"],
    pathFragments: ["botanicals/tree10", "tree10.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/BOTANICALS/Tree11",
    names: ["tree11", "tree 11"],
    pathFragments: ["botanicals/tree11", "tree11.svg"],
  },
  {
    libraryKey: "MANUAL/king-bed",
    names: ["king bed"],
    pathFragments: ["dubbelsang ny-01", "double bed 2"],
  },
  {
    libraryKey: "MANUAL/single-bed",
    names: ["single bed"],
    pathFragments: ["enkelsang ny-01", "single bed"],
  },
  {
    libraryKey: "FLOOR_PLAN/OFFICE/Screen3",
    names: ["screen3", "screen 3"],
    pathFragments: ["office/screen3", "screen3.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/OFFICE/Screen4",
    names: ["screen4", "screen 4"],
    pathFragments: ["office/screen4", "screen4.svg"],
  },
  {
    libraryKey: "MANUAL/basin",
    names: ["basin"],
    pathFragments: ["handfat ny-01", "basin"],
  },
  {
    libraryKey: "MANUAL/bathtub",
    names: ["bathtub"],
    pathFragments: ["badkar 1 ny-01", "bathtub"],
  },
  {
    libraryKey: "MANUAL/shower",
    names: ["shower"],
    pathFragments: ["dusch", "shower"],
  },
  {
    libraryKey: "MANUAL/toilet",
    names: ["toilet"],
    pathFragments: ["toa ny-01", "toilet"],
  },
  {
    libraryKey: "FLOOR_PLAN/KITCHEN/Cabinet5",
    names: ["cabinet5", "cabinet 5"],
    pathFragments: ["kitchen/cabinet5", "cabinet5.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/KITCHEN/Fridge4",
    names: ["fridge4", "fridge 4"],
    pathFragments: ["kitchen/fridge4", "fridge4.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/KITCHEN/Fridge5",
    names: ["fridge5", "fridge 5"],
    pathFragments: ["kitchen/fridge5", "fridge5.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/KITCHEN/Sink",
    names: ["sink"],
    pathFragments: ["kitchen/sink.svg", "sink.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/KITCHEN/Sink4",
    names: ["sink4", "sink 4"],
    pathFragments: ["kitchen/sink4", "sink4.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/KITCHEN/Sink5",
    names: ["sink5", "sink 5"],
    pathFragments: ["kitchen/sink5", "sink5.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/KITCHEN/Stove",
    names: ["stove"],
    pathFragments: ["kitchen/stove.svg", "stove.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/KITCHEN/Stove3",
    names: ["stove3", "stove 3"],
    pathFragments: ["kitchen/stove3", "stove3.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/KITCHEN/Stove4",
    names: ["stove4", "stove 4"],
    pathFragments: ["kitchen/stove4", "stove4.svg"],
  },
  {
    libraryKey: "FLOOR_PLAN/KITCHEN/Stove5",
    names: ["stove5", "stove 5"],
    pathFragments: ["kitchen/stove5", "stove5.svg"],
  },
  {
    libraryKey: "MANUAL/plain-table",
    names: ["plain table"],
    pathFragments: ["plain table"],
  },
  {
    libraryKey: "MANUAL/2-chair-table",
    names: ["2 chair table"],
    pathFragments: ["2-bord ny-01", "2 chair table"],
  },
  {
    libraryKey: "MANUAL/4-chair-table",
    names: ["4 chair table"],
    pathFragments: ["4-bord ny-01", "4 chair table"],
  },
  {
    libraryKey: "MANUAL/6-chair-table",
    names: ["6 chair table"],
    pathFragments: ["6 bord ny-01", "6 chair table"],
  },
  {
    libraryKey: "MANUAL/armchair-dining",
    names: ["armchair"],
    pathFragments: ["stol 2-01", "armchair 2"],
  },
  {
    libraryKey: "MANUAL/chair",
    names: ["chair"],
    pathFragments: ["stol ny-01", "chair"],
  },
  {
    libraryKey: "MANUAL/plain-round-table",
    names: ["plain round table"],
    pathFragments: ["plain round table"],
  },
  {
    libraryKey: "MANUAL/round-table",
    names: ["round table"],
    pathFragments: ["runt bord ny-01", "round table"],
  },
  {
    libraryKey: "FLOOR_PLAN/SYMBOLS & SIGNS/Ungrounded power outlet",
    names: ["ungrounded power outlet"],
    pathFragments: ["ungrounded power outlet"],
  },
];

export const PRIMITIVE_SYMBOL_MATERIAL_SLOT_MANIFEST: Partial<
  Record<PrimitiveSymbol3DModelKey, FloorPlan3DMaterialSlot[]>
> = {
  "living:sofa-2-seat": ["furnitureFabric", "furnitureMetal"],
  "living:sofa-3-seat": ["furnitureFabric", "furnitureMetal"],
  "living:sofa-modular": ["furnitureFabric", "furnitureMetal"],
  "living:sofa-chaise": ["furnitureFabric", "furnitureWood"],
  "living:lounge-chair-1": ["furnitureFabric", "furnitureMetal"],
  "living:lounge-chair-2": ["furnitureFabric", "furnitureMetal"],
  "living:armchair": ["furnitureFabric", "furnitureMetal"],
  "living:coffee-table-octagonal": ["furnitureWood", "furnitureMetal"],
  "living:coffee-table-pentagonal": ["furnitureWood", "furnitureMetal"],
  "living:coffee-table-hexagonal": ["furnitureWood", "furnitureMetal"],
  "living:coffee-table-organic": ["furnitureWood"],
  "living:coffee-table-oval": ["furnitureWood"],
  "living:screen": ["furnitureScreen", "furnitureMetal"],
  "living:screen-wide": ["furnitureScreen", "furnitureMetal"],
  "botanical:bush-round": ["botanicalFoliage", "botanicalFoliageDeep"],
  "botanical:bush-hedge": ["botanicalFoliage", "botanicalFoliageDeep"],
  "botanical:bush-organic": ["botanicalFoliage", "botanicalFoliageDeep"],
  "botanical:plant": ["botanicalFoliage", "botanicalPot"],
  "botanical:tree-canopy": ["botanicalFoliage", "botanicalBranch"],
  "botanical:tree-branch": ["botanicalFoliage", "botanicalBranch"],
  "bedroom:bed-king": ["furnitureFabric", "furnitureMetal"],
  "bedroom:bed-single": ["furnitureFabric", "furnitureMetal"],
  "office:screen": ["furnitureScreen", "furnitureMetal"],
  "office:screen-wide": ["furnitureScreen", "furnitureMetal"],
  "kitchen:cabinet": ["doorPanel", "furnitureWood", "furnitureMetal"],
  "kitchen:fridge-k": ["furnitureMetal", "doorPanel", "doorHandle"],
  "kitchen:fridge-f": ["furnitureMetal", "doorPanel", "doorHandle"],
  "kitchen:sink-double": ["doorPanel", "furnitureMetal"],
  "kitchen:sink-drainboard": ["doorPanel", "furnitureMetal", "doorHardware"],
  "kitchen:sink-single": ["doorPanel", "furnitureMetal"],
  "kitchen:stove": ["furnitureMetal", "furnitureGlass", "furnitureScreen"],
  "kitchen:stove-compact": [
    "furnitureMetal",
    "furnitureGlass",
    "furnitureScreen",
  ],
  "dining:table-plain": ["furnitureWood", "furnitureMetal"],
  "dining:table-2-chair": ["furnitureWood", "furnitureMetal"],
  "dining:table-4-chair": ["furnitureWood", "furnitureMetal"],
  "dining:table-6-chair": ["furnitureWood", "furnitureMetal"],
  "dining:armchair": ["furnitureMetal"],
  "dining:chair": ["furnitureMetal"],
  "dining:round-table-plain": ["furnitureWood", "furnitureMetal"],
  "dining:round-table": ["furnitureWood", "furnitureMetal"],
  "dining:smart-table-rectangle": ["furnitureWood", "furnitureMetal"],
  "dining:smart-table-round": ["furnitureWood", "furnitureMetal"],
  "living:bookshelf": ["furnitureWood"],
  "symbols:curtain": ["furnitureFabric", "furnitureMetal"],
  "symbols:bonnie": ["furnitureFabric"],
  "symbols:hangers-1": ["furnitureWood", "furnitureMetal", "furnitureFabric"],
  "symbols:hangers-2": ["furnitureWood", "furnitureMetal", "furnitureFabric"],
  "symbols:hangers-3": ["furnitureWood", "furnitureMetal", "furnitureFabric"],
  "symbols:hangers-4": ["furnitureWood", "furnitureMetal", "furnitureFabric"],
  "symbols:lighting": ["furnitureMetal", "furnitureGlass"],
  "symbols:spotlight": ["furnitureMetal", "furnitureGlass"],
  "symbols:lighting-fixture": ["furnitureMetal", "furnitureGlass"],
  "symbols:ungrounded-power-outlet": [
    "doorPanel",
    "doorFrame",
    "furnitureScreen",
  ],
};

export const PRIMITIVE_SYMBOL_3D_DEFAULTS: Record<
  PrimitiveSymbol3DModelKey,
  PrimitiveSymbol3DDefaults
> = {
  "living:sofa-2-seat": defaults("living:sofa-2-seat", 1.5, 0.853, 0.894),
  "living:sofa-3-seat": defaults("living:sofa-3-seat", 2, 0.857, 0.906),
  "living:sofa-modular": defaults("living:sofa-modular", 2.4, 2.136, 0.91),
  "living:sofa-chaise": defaults("living:sofa-chaise", 1.8, 0.867, 0.936),
  "living:lounge-chair-1": defaults(
    "living:lounge-chair-1",
    0.801,
    0.743,
    0.741,
  ),
  "living:lounge-chair-2": defaults(
    "living:lounge-chair-2",
    0.699,
    0.703,
    0.757,
  ),
  "living:armchair": defaults("living:armchair", 0.6, 0.614, 0.906),
  "living:coffee-table-octagonal": defaults(
    "living:coffee-table-octagonal",
    0.9,
    0.884,
    0.42,
  ),
  "living:coffee-table-pentagonal": defaults(
    "living:coffee-table-pentagonal",
    0.946,
    0.939,
    0.42,
  ),
  "living:coffee-table-hexagonal": defaults(
    "living:coffee-table-hexagonal",
    1.039,
    0.906,
    0.42,
  ),
  "living:coffee-table-organic": defaults(
    "living:coffee-table-organic",
    1.092,
    0.771,
    0.42,
  ),
  "living:coffee-table-oval": defaults(
    "living:coffee-table-oval",
    1.2,
    0.485,
    0.42,
  ),
  "living:screen": defaults("living:screen", 1, 0.17, 0.72),
  "living:screen-wide": defaults("living:screen-wide", 1.5, 0.162, 0.78),
  "botanical:bush-round": defaults("botanical:bush-round", 0.9, 0.869, 0.7),
  "botanical:bush-hedge": defaults("botanical:bush-hedge", 2.507, 0.9, 0.75),
  "botanical:bush-organic": defaults(
    "botanical:bush-organic",
    1.3,
    1.186,
    0.85,
  ),
  "botanical:plant": defaults("botanical:plant", 0.712, 0.7, 0.75),
  "botanical:tree-canopy": defaults("botanical:tree-canopy", 2.8, 3.406, 2.6),
  "botanical:tree-branch": defaults("botanical:tree-branch", 2.6, 2.584, 2.2),
  "bedroom:bed-king": defaults("bedroom:bed-king", 2.1, 1.8, 0.67),
  "bedroom:bed-single": defaults("bedroom:bed-single", 2.1, 0.9, 0.67),
  "office:screen": defaults("office:screen", 1, 0.17, 0.72),
  "office:screen-wide": defaults("office:screen-wide", 1.5, 0.162, 0.78),
  "bathroom:basin": defaults("bathroom:basin", 0.6, 0.45, 0.95),
  "bathroom:bathtub": defaults("bathroom:bathtub", 1.828, 0.8, 0.63),
  "bathroom:shower": defaults("bathroom:shower", 0.9, 0.9, 2.1),
  "bathroom:toilet": defaults("bathroom:toilet", 0.65, 0.35, 0.95),
  "kitchen:cabinet": defaults("kitchen:cabinet", 0.599, 0.6, 0.9),
  "kitchen:fridge-k": defaults("kitchen:fridge-k", 0.6, 0.6, 1.85),
  "kitchen:fridge-f": defaults("kitchen:fridge-f", 0.6, 0.6, 1.85),
  "kitchen:sink-double": defaults("kitchen:sink-double", 0.939, 0.5, 0.9),
  "kitchen:sink-drainboard": defaults(
    "kitchen:sink-drainboard",
    0.884,
    0.5,
    0.9,
  ),
  "kitchen:sink-single": defaults("kitchen:sink-single", 0.573, 0.5, 0.9),
  "kitchen:stove": defaults("kitchen:stove", 0.904, 0.6, 0.9),
  "kitchen:stove-compact": defaults("kitchen:stove-compact", 0.599, 0.6, 0.9),
  "kitchen:stove-placeholder-angled": defaults(
    "kitchen:stove-placeholder-angled",
    0.6,
    0.6,
    0.9,
  ),
  "kitchen:stove-placeholder-simple": defaults(
    "kitchen:stove-placeholder-simple",
    0.6,
    0.6,
    0.9,
  ),
  "dining:table-plain": defaults(
    "dining:table-plain",
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/plain-table"].symLength,
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/plain-table"].symHeight,
    0.78,
  ),
  "dining:table-2-chair": defaults(
    "dining:table-2-chair",
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/2-chair-table"].symLength,
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/2-chair-table"].symHeight,
    0.78,
  ),
  "dining:table-4-chair": defaults(
    "dining:table-4-chair",
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/4-chair-table"].symLength,
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/4-chair-table"].symHeight,
    0.78,
  ),
  "dining:table-6-chair": defaults(
    "dining:table-6-chair",
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/6-chair-table"].symLength,
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/6-chair-table"].symHeight,
    0.78,
  ),
  "dining:armchair": defaults(
    "dining:armchair",
    DINING_ARMCHAIR_SIZE_M,
    DINING_ARMCHAIR_SIZE_M,
    0.76,
  ),
  "dining:chair": defaults(
    "dining:chair",
    DINING_CHAIR_DEPTH_M,
    DINING_CHAIR_WIDTH_M,
    0.82,
  ),
  "dining:round-table-plain": defaults(
    "dining:round-table-plain",
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/plain-round-table"].symLength,
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/plain-round-table"].symHeight,
    0.78,
  ),
  "dining:round-table": defaults(
    "dining:round-table",
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/round-table"].symLength,
    DINING_SYMBOL_LIBRARY_DIMENSIONS["MANUAL/round-table"].symHeight,
    0.78,
  ),
  "dining:smart-table-rectangle": defaults(
    "dining:smart-table-rectangle",
    0.9,
    1.4,
    0.76,
  ),
  "dining:smart-table-round": defaults(
    "dining:smart-table-round",
    1.5,
    1.5,
    0.76,
  ),
  "living:bookshelf": defaults(
    "living:bookshelf",
    BOOK_SHELF_DEFAULT_WIDTH_M,
    BOOK_SHELF_DEFAULT_DEPTH_M,
    createDefaultBookShelfConfig().height,
  ),
  "symbols:curtain": defaults("symbols:curtain", 1.0, 0.064, 2.4),
  "symbols:bonnie": defaults(
    "symbols:bonnie",
    BONNIE_WIDTH_M,
    BONNIE_DEPTH_M,
    BONNIE_HEIGHT_M,
  ),
  "symbols:hangers-1": defaults("symbols:hangers-1", 0.782, 0.6, 1.8),
  "symbols:hangers-2": defaults("symbols:hangers-2", 1.044, 0.6, 1.8),
  "symbols:hangers-3": defaults("symbols:hangers-3", 1.304, 0.6, 1.8),
  "symbols:hangers-4": defaults("symbols:hangers-4", 1.566, 0.6, 1.8),
  "symbols:lighting": defaults("symbols:lighting", 0.643, 0.2, 0.8),
  "symbols:spotlight": defaults("symbols:spotlight", 0.2, 0.2, 0.15),
  "symbols:lighting-fixture": defaults(
    "symbols:lighting-fixture",
    0.2,
    0.2,
    0.15,
  ),
  "symbols:ungrounded-power-outlet": defaults(
    "symbols:ungrounded-power-outlet",
    0.209,
    0.2,
    0.085,
    0.25,
  ),
};

/**
 * Functional heights from the Swedish symbol specification. These are not
 * always the overall model height: a backrest, pillow, faucet, or cistern may
 * extend above the measured surface.
 */
export const PRIMITIVE_SYMBOL_3D_REFERENCE_HEIGHTS: Partial<
  Record<PrimitiveSymbol3DModelKey, PrimitiveSymbol3DReferenceHeight>
> = {
  "living:sofa-2-seat": { measurement: "seatTop", height: 0.45 },
  "living:sofa-3-seat": { measurement: "seatTop", height: 0.45 },
  "living:sofa-modular": { measurement: "seatTop", height: 0.45 },
  "living:sofa-chaise": { measurement: "seatTop", height: 0.45 },
  "living:lounge-chair-1": { measurement: "seatTop", height: 0.45 },
  "living:lounge-chair-2": { measurement: "seatTop", height: 0.45 },
  "living:armchair": { measurement: "seatTop", height: 0.45 },
  "living:coffee-table-octagonal": {
    measurement: "tabletop",
    height: 0.42,
  },
  "living:coffee-table-pentagonal": {
    measurement: "tabletop",
    height: 0.42,
  },
  "living:coffee-table-hexagonal": {
    measurement: "tabletop",
    height: 0.42,
  },
  "living:coffee-table-organic": { measurement: "tabletop", height: 0.42 },
  "living:coffee-table-oval": { measurement: "tabletop", height: 0.42 },
  "bedroom:bed-king": { measurement: "mattressTop", height: 0.55 },
  "bedroom:bed-single": { measurement: "mattressTop", height: 0.55 },
  "bathroom:basin": { measurement: "basinTop", height: 0.85 },
  "bathroom:bathtub": { measurement: "rimTop", height: 0.6 },
  "bathroom:toilet": { measurement: "seatTop", height: 0.48 },
};

/**
 * Historical defaults for fields that changed. When the version is bumped,
 * add the outgoing value under its version before changing the live default.
 */
const PRIMITIVE_SYMBOL_3D_HEIGHT_HISTORY: Partial<
  Record<PrimitiveSymbol3DModelKey, Readonly<Record<number, number>>>
> = {
  "living:sofa-2-seat": { 1: 0.74, 2: 0.74 },
  "living:sofa-3-seat": { 1: 0.76, 2: 0.76 },
  "living:sofa-modular": { 1: 0.78, 2: 0.78 },
  "living:sofa-chaise": { 1: 0.76, 2: 0.76 },
  "living:lounge-chair-1": { 1: 0.56, 2: 0.56 },
  "living:lounge-chair-2": { 1: 0.59, 2: 0.59 },
  "living:armchair": { 1: 0.76, 2: 0.76 },
  "living:coffee-table-octagonal": { 1: 0.46, 2: 0.42 },
  "living:coffee-table-pentagonal": { 1: 0.46, 2: 0.42 },
  "living:coffee-table-hexagonal": { 1: 0.46, 2: 0.42 },
  "living:coffee-table-organic": { 1: 0.38, 2: 0.42 },
  "living:coffee-table-oval": { 1: 0.38, 2: 0.42 },
  "bedroom:bed-king": { 1: 0.62, 2: 0.625, 3: 0.625 },
  "bedroom:bed-single": { 1: 0.58, 2: 0.625, 3: 0.625 },
  "bathroom:basin": { 1: 0.9, 2: 0.95 },
  "bathroom:bathtub": { 1: 0.55, 2: 0.63 },
  "bathroom:toilet": {
    1: 0.72,
    2: 0.72,
    3: 0.72,
    4: 0.72,
    5: 0.82,
    6: 1,
    7: 1.03,
  },
};

/**
 * Original vertical design envelopes for primitives whose current height adds
 * a raised surface or crown. Keeping the authored envelope separate preserves
 * the surrounding frame, backrest, and bedding proportions.
 */
const PRIMITIVE_SYMBOL_3D_AUTHORED_HEIGHTS: Partial<
  Record<PrimitiveSymbol3DModelKey, number>
> = {
  "living:sofa-2-seat": 0.74,
  "living:sofa-3-seat": 0.76,
  "living:sofa-modular": 0.78,
  "living:sofa-chaise": 0.76,
  "living:lounge-chair-1": 0.56,
  "living:lounge-chair-2": 0.59,
  "living:armchair": 0.76,
  // The bed's frame and bedding keep their previous proportions; the current
  // overall envelope is taller only because the pillow crown is now 150 mm.
  "bedroom:bed-king": 0.625,
  "bedroom:bed-single": 0.625,
};

export function isPrimitiveSymbol3DKey(
  key: string | undefined,
): key is PrimitiveSymbol3DModelKey {
  return Boolean(
    key && PRIMITIVE_SYMBOL_MODEL_KEYS.has(key as PrimitiveSymbol3DModelKey),
  );
}

export function getPrimitiveSymbol3DModelKey(
  key: string | undefined,
): PrimitiveSymbol3DModelKey | undefined {
  if (!key) return undefined;
  if (isPrimitiveSymbol3DKey(key)) return key;
  return PRIMITIVE_SYMBOL_LIBRARY_MODEL_KEYS[key];
}

export function isPrimitiveTable3DModelKey(
  key: string | undefined,
): boolean {
  const modelKey = getPrimitiveSymbol3DModelKey(key);
  return Boolean(modelKey && PRIMITIVE_TABLE_3D_MODEL_KEYS.has(modelKey));
}

/**
 * Facade / elevation (Fasad) library symbols are 2D elevation drawings only.
 * They must never resolve to floor-plan 3D primitives via shared labels
 * (e.g. "2 seat sofa") or appear as meshes in the 3D view.
 */
export function isElevationLibrarySymbol(
  symbol: PrimitiveSymbolLike,
): boolean {
  const libraryKey = symbol.libraryKey?.trim();
  if (libraryKey?.startsWith("ELEVATION/")) return true;
  const svgPath = symbol.svgPath?.trim();
  return Boolean(svgPath?.startsWith("ELEVATION/"));
}

export function getPrimitiveSymbol3DModelKeyForSymbol(
  symbol: PrimitiveSymbolLike,
): PrimitiveSymbol3DModelKey | undefined {
  // Elevation/Fasad props stay 2D-only even when names match floor-plan aliases.
  if (isElevationLibrarySymbol(symbol)) return undefined;

  const explicitKey = getPrimitiveSymbol3DModelKey(
    symbol.threeD?.modelKey ?? symbol.libraryKey,
  );
  if (explicitKey) return explicitKey;

  const libraryKey = getPrimitiveSymbol3DLibraryKeyForSymbol(symbol);
  return libraryKey
    ? PRIMITIVE_SYMBOL_LIBRARY_MODEL_KEYS[libraryKey]
    : undefined;
}

/**
 * Recovers the current symbol-library tag for floorplans saved before placed
 * symbols persisted `libraryKey`. Legacy SVG names/paths are preferred over a
 * saved primitive model key because they identify the original library row.
 */
export function getPrimitiveSymbol3DLibraryKeyForSymbol(
  symbol: PrimitiveSymbolLike,
): string | undefined {
  if (isElevationLibrarySymbol(symbol)) return undefined;

  const explicitLibraryKey = symbol.libraryKey?.trim();
  if (
    explicitLibraryKey &&
    Object.prototype.hasOwnProperty.call(
      PRIMITIVE_SYMBOL_LIBRARY_MODEL_KEYS,
      explicitLibraryKey,
    )
  ) {
    return explicitLibraryKey;
  }

  const legacyAlias = getLegacyPrimitiveSymbolAlias(symbol);
  if (legacyAlias) return legacyAlias.libraryKey;

  const savedModelKey = getPrimitiveSymbol3DModelKey(symbol.threeD?.modelKey);
  if (!savedModelKey) return undefined;
  const matchingLibraryKeys = Object.entries(
    PRIMITIVE_SYMBOL_LIBRARY_MODEL_KEYS,
  ).filter(([, modelKey]) => modelKey === savedModelKey);
  return matchingLibraryKeys.length === 1
    ? matchingLibraryKeys[0][0]
    : undefined;
}

export function getPrimitiveSymbolMaterialSlots(
  key: string | undefined,
): FloorPlan3DMaterialSlot[] {
  const modelKey = getPrimitiveSymbol3DModelKey(key);
  return modelKey
    ? (PRIMITIVE_SYMBOL_MATERIAL_SLOT_MANIFEST[modelKey] ?? [])
    : [];
}

export function getPrimitiveSymbol3DDefaults(
  key: string | undefined,
): PrimitiveSymbol3DDefaults | undefined {
  const modelKey = getPrimitiveSymbol3DModelKey(key);
  return modelKey ? PRIMITIVE_SYMBOL_3D_DEFAULTS[modelKey] : undefined;
}

export function getScaledPrimitiveSymbol3DReferenceHeight(
  key: string | undefined,
  overallHeight: number,
): number | undefined {
  const modelKey = getPrimitiveSymbol3DModelKey(key);
  if (!modelKey) return undefined;

  const reference = PRIMITIVE_SYMBOL_3D_REFERENCE_HEIGHTS[modelKey];
  const defaultHeight = PRIMITIVE_SYMBOL_3D_DEFAULTS[modelKey].height;
  if (!reference || defaultHeight <= 0) return undefined;
  return reference.height * (overallHeight / defaultHeight);
}

export function getScaledPrimitiveSymbol3DAuthoredHeight(
  key: string | undefined,
  overallHeight: number,
): number {
  const modelKey = getPrimitiveSymbol3DModelKey(key);
  if (!modelKey) return overallHeight;

  const currentDefaultHeight = PRIMITIVE_SYMBOL_3D_DEFAULTS[modelKey].height;
  const authoredHeight =
    PRIMITIVE_SYMBOL_3D_AUTHORED_HEIGHTS[modelKey] ?? currentDefaultHeight;
  if (currentDefaultHeight <= 0) return overallHeight;
  return authoredHeight * (overallHeight / currentDefaultHeight);
}

export function createPrimitiveSymbol3DMetadata(
  key: string,
  width?: number,
  depth?: number,
): Symbol3DMetadata | undefined {
  const defaults = getPrimitiveSymbol3DDefaults(key);
  if (!defaults) return undefined;

  return {
    modelKey: defaults.modelKey,
    primitiveDefaultsVersion: PRIMITIVE_SYMBOL_3D_DEFAULTS_VERSION,
    height: defaults.height,
    baseElevation: defaults.baseElevation,
    collision: defaults.collision,
    dimensions: {
      width: width ?? defaults.dimensions.width,
      depth: depth ?? defaults.dimensions.depth,
      height: defaults.height,
    },
  };
}

/**
 * Refreshes persisted library-owned primitive metadata when the library entry
 * now points at a different primitive model. Floorplans intentionally persist
 * instance dimensions, transforms, and material choices. Only library-owned
 * primitive defaults are refreshed from the current symbol-library definition.
 */
export function reconcileLoadedLibraryPrimitive3D<
  T extends PrimitiveSymbolLike,
>(symbol: T): T {
  const resolvedLibraryKey = getPrimitiveSymbol3DLibraryKeyForSymbol(symbol);
  const currentModelKey = getPrimitiveSymbol3DModelKey(resolvedLibraryKey);
  if (!currentModelKey) return symbol;

  const symbolWithCurrentLibraryKey = (
    symbol.libraryKey?.trim()
      ? symbol
      : { ...symbol, libraryKey: resolvedLibraryKey }
  ) as T;

  // Products and uploaded models are explicit instance choices. Modern writes
  // mark them as custom; the reference checks also protect older floorplans.
  if (
    symbol.threeD?.sourceMode === "custom" ||
    symbol.threeD?.product?.productId ||
    symbol.threeD?.userModel?.modelId
  ) {
    return symbolWithCurrentLibraryKey;
  }

  const currentMetadata = createPrimitiveSymbol3DMetadata(
    currentModelKey,
    symbol.svgLength ?? symbol.polygonWidth,
    symbol.svgHeight ?? symbol.polygonHeight,
  );
  if (!currentMetadata) return symbolWithCurrentLibraryKey;

  const savedModelKey = getPrimitiveSymbol3DModelKey(symbol.threeD?.modelKey);
  const savedDefaultsVersion = symbol.threeD?.primitiveDefaultsVersion ?? 1;
  const savedHeight =
    symbol.threeD?.height ?? symbol.threeD?.dimensions?.height;
  const savedDefaultHeight = savedModelKey
    ? (PRIMITIVE_SYMBOL_3D_HEIGHT_HISTORY[savedModelKey]?.[
        savedDefaultsVersion
      ] ?? PRIMITIVE_SYMBOL_3D_DEFAULTS[savedModelKey].height)
    : undefined;
  const shouldRefreshHeight =
    savedHeight === undefined ||
    (savedDefaultsVersion < PRIMITIVE_SYMBOL_3D_DEFAULTS_VERSION &&
      savedDefaultHeight !== undefined &&
      dimensionsAreEqual(savedHeight, savedDefaultHeight));
  const nextHeight = shouldRefreshHeight ? currentMetadata.height : savedHeight;
  const nextDimensions = {
    ...currentMetadata.dimensions,
    ...symbol.threeD?.dimensions,
    height: nextHeight,
  };

  return {
    ...symbolWithCurrentLibraryKey,
    threeD: {
      ...symbol.threeD,
      ...currentMetadata,
      primitiveDefaultsVersion: PRIMITIVE_SYMBOL_3D_DEFAULTS_VERSION,
      height: nextHeight,
      baseElevation:
        symbol.threeD?.baseElevation ?? currentMetadata.baseElevation,
      dimensions: nextDimensions,
    },
  };
}

function dimensionsAreEqual(actual: number, expected: number): boolean {
  return Math.abs(actual - expected) <= 1e-6;
}

function defaults(
  modelKey: PrimitiveSymbol3DModelKey,
  width: number,
  depth: number,
  height: number,
  baseElevation = 0,
): PrimitiveSymbol3DDefaults {
  return {
    modelKey,
    height,
    baseElevation,
    collision: "box",
    dimensions: {
      width,
      depth,
      height,
    },
  };
}

function hasLegacySvgIdentity(symbol: PrimitiveSymbolLike): boolean {
  return (
    typeof symbol.svgPath === "string" ||
    typeof symbol.svgLength === "number" ||
    typeof symbol.svgHeight === "number"
  );
}

function getLegacyPrimitiveSymbolAlias(
  symbol: PrimitiveSymbolLike,
): LegacyPrimitiveSymbolAlias | undefined {
  if (!hasLegacySvgIdentity(symbol)) return undefined;

  const normalizedName = normalizeLegacySymbolText(symbol.name);
  const normalizedPath = normalizeLegacySymbolText(symbol.svgPath);
  const candidates = LEGACY_PRIMITIVE_SYMBOL_ALIASES.filter((alias) => {
    const nameMatches =
      normalizedName.length > 0 &&
      alias.names.some(
        (name) => normalizeLegacySymbolText(name) === normalizedName,
      );
    const pathMatches =
      normalizedPath.length > 0 &&
      alias.pathFragments.some((fragment) =>
        normalizedPath.includes(normalizeLegacySymbolText(fragment)),
      );
    return nameMatches || pathMatches;
  });

  return candidates.length <= 1
    ? candidates[0]
    : candidates.find((candidate) =>
        legacySymbolDimensionsMatch(symbol, candidate.libraryKey),
      );
}

function legacySymbolDimensionsMatch(
  symbol: PrimitiveSymbolLike,
  libraryKey: keyof typeof PRIMITIVE_SYMBOL_LIBRARY_MODEL_KEYS,
): boolean {
  const defaults = getPrimitiveSymbol3DDefaults(libraryKey);
  if (!defaults) return false;

  return (
    dimensionMatches(symbol.svgLength, defaults.dimensions.width) &&
    dimensionMatches(symbol.svgHeight, defaults.dimensions.depth)
  );
}

function dimensionMatches(
  actual: number | undefined,
  expected: number | undefined,
): boolean {
  if (actual === undefined || expected === undefined) return false;
  return Math.abs(actual - expected) <= Math.max(0.03, expected * 0.06);
}

function normalizeLegacySymbolText(value: string | undefined): string {
  if (!value) return "";

  try {
    return normalizeComparableText(decodeURIComponent(value));
  } catch {
    return normalizeComparableText(value);
  }
}

function normalizeComparableText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\\/g, "/")
    .replace(/\s+/g, " ");
}
